import { create } from "zustand";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";
import {
  buildDrivingReport,
  toDrivingPoint,
} from "../../pages/reports/driving-section/drivingReportRule";

export const DRIVING_COLORS = [
  "#2563EB",
  "#DB2777",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#0891B2",
  "#DC2626",
  "#65A30D",
];

export const MAP_EVENTS_WITH_CODE: Record<string, string> = {
  "1": "Alerta de velocidad",
  "2": "Movimiento no autorizado",
  "3": "Motor encendido",
  "4": "Motor apagado",
  "5": "Pánico",
  "6": "Posición",
  "7": "Enganche",
  "8": "Liberación",
  "9": "Batería baja",
  "10": "Inicio de carga",
  "11": "Fin de carga",
  "12": "Poleo",
  "13": "Llamada de poleo",
  "14": "Puerta abierta",
  "15": "Puerta cerrada",
  "16": "Bloqueo de motor activo",
  "17": "Bloqueo de motor desactivado",
  "18": "Desconexión de energía",
  "19": "Conexión de energía",
  "20": "Temperatura",
  "21": "Gas",
  "22": "Dispositivo apagado",
  "23": "Entrada a zona",
  "24": "Salida de zona",
  "25": "Giro",
  "26": "Movimiento activo",
  "27": "Movimiento inactivo",
  "28": "Movimiento en zona",
  "29": "Posición con energía de respaldo",
  "30": "Reinicio periódico",
  "31": "Desenganche",
  "32": "Candado magnético abierto",
  "33": "Candado magnético cerrado",
  "34": "Aceleración",
  "35": "Frenado",
  "36": "Detección de interferencia",
  "37": "Remolque",
  "38": "Reversa",
  "40": "Recarga de combustible",
  "41": "Extracción de combustible",
  "42": "Reversa inactiva",
  "43": "Otro",
};

export interface DrivingRow {
  ts: number;
  estadoMotor: string;
  /** null = sin velocidad ni estimación por odómetro ("N/A") */
  velocidad: number | null;
  lat: number;
  lon: number;
  evento: string;
}

export interface DrivingResult {
  deviceId: string;
  vehicleName: string;
  color: string;
  rows: DrivingRow[];
  totalPoints: number;
  idleCount: number;
  totalIdleTime: number;
  loading: boolean;
  error: string | null;
}

export interface DrivingDevice {
  deviceId: string;
  vehicleName: string;
  /** marca/modelo del equipo: deciden si se lee la bandera de ralentí (Ruptela ECO5/TRACE5) */
  brandName?: string | null;
  brandModel?: string | null;
}

interface ReportDrivingState {
  results: Record<string, DrivingResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
}

interface ReportDrivingActions {
  loadRange: (
    devices: DrivingDevice[],
    from: string,
    to: string,
    opts: { maxSpeed: number; maxRpm?: number; tz?: string },
  ) => Promise<void>;
  clear: () => void;
}

type ReportDrivingStore = ReportDrivingState & ReportDrivingActions;

export const useReportDrivingStore = create<ReportDrivingStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const maxSpeed = opts.maxSpeed || 120;
    const maxRpm = opts.maxRpm || 2500;
    const order = devices.map((d) => d.deviceId);

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);

    const initial: Record<string, DrivingResult> = {};
    devices.forEach((d, i) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        color: DRIVING_COLORS[i % DRIVING_COLORS.length],
        rows: [],
        totalPoints: 0,
        idleCount: 0,
        totalIdleTime: 0,
        loading: true,
        error: null,
      };
    });

    set({ results: initial, order, loading: true, from, to, tz: resolvedTz });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      // Mismo cálculo que el PDF/Excel (drivingReportRule.js del backend).
      const parsed = points.map((p) => toDrivingPoint(p, d.brandName, d.brandModel));
      const { rows: ruleRows, totalPoints, idleCount, totalIdleTime } = buildDrivingReport(parsed, {
        maxSpeed,
        maxRpm,
      });
      const rows: DrivingRow[] = ruleRows.map(({ point, speed, ralenti, eventCode }) => ({
        ts: point.ts,
        estadoMotor: point.ignition ? "encendido" : "apagado",
        velocidad: speed,
        lat: point.lat,
        lon: point.lon,
        evento: ralenti ? "ralenti" : MAP_EVENTS_WITH_CODE[eventCode] || eventCode,
      }));

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;
        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              rows,
              totalPoints,
              idleCount,
              totalIdleTime,
              loading: false,
              error:
                parsed.length === 0 && failedChunks > 0
                  ? "No se pudo obtener la conducción (servicio no disponible)."
                  : parsed.length === 0
                    ? "Sin datos en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({ results: {}, order: [], loading: false, from: null, to: null, tz: null }),
}));
